import React, { useEffect } from "react";
import { ArcButton, ArcStack, ArcText } from "@src/core/ui";
import { FormState, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { setZoneMapLocationUponCreation, useCreateSafeLandZoneMutation } from "@src/features/zones/redux";
import { routesConfig } from "@src/core/routes";
import { SafeLandZoneForm } from "@src/features/zones/components/zone-form/safe-land";
import { useDispatch, useSelector } from "react-redux";
import { ZoneType } from "@src/core/features";
import { selectMapLocation } from "@src/core/controllers";
import { VARIANT_SAFE_LAND } from "@src/features/zones/pages/zones-page/types";
import { ICreateSafeLandZoneForm } from "./types";

export const SafeLandCreateZonePage: React.FC = () => {
  const defaultValues: ICreateSafeLandZoneForm = {
    zoneName: "",
    zoneDescription: "",
    latitude: "",
    longitude: "",
    address: "",
    zoneAltitude: 0,
    zoneType: ZoneType.TEMPORARY,
  };

  const { control, setValue, register, getValues, formState: { errors }, handleSubmit, watch } = useForm<ICreateSafeLandZoneForm>({ defaultValues });

  const navigate = useNavigate();
  const [createZone, { isLoading }] = useCreateSafeLandZoneMutation();
  const dispatch = useDispatch();
  const location = useSelector(selectMapLocation);

  useEffect(() => {
    const radius = 0;
    dispatch(setZoneMapLocationUponCreation({
      id: watch("id") ?? 0,
      latitude: location?.latitude ?? 0,
      longitude: location?.longitude ?? 0,
      zoneName: watch("zoneName") ?? "",
      radius,
      type: watch("zoneType") ?? ZoneType.TEMPORARY,
      variant: VARIANT_SAFE_LAND,
    }));
    return () => {
      dispatch(setZoneMapLocationUponCreation(null));
    };
  }, [location, watch, dispatch]);

  const onCancel = () => {
    navigate(-1);
    dispatch(setZoneMapLocationUponCreation(null));
  };

  const onSubmit = async (data: ICreateSafeLandZoneForm) => {
    try {
      await createZone({
        name: data.zoneName,
        description: data.zoneDescription,
        centerLatitude: Number(data.latitude),
        centerLongitude: Number(data.longitude),
        locationName: data.address,
        altitudeMeters: data.zoneAltitude ?? 0,
        isActive: true,
      }).unwrap();
      navigate(`${routesConfig.zones}?tab=${VARIANT_SAFE_LAND}`);
      dispatch(setZoneMapLocationUponCreation(null));
    } catch (error) {
      console.error("Failed to create safe-land zone:", error);
    }
  };

  return (
    <ArcStack height="100%" direction="column" justifyContent="space-between">
      <ArcStack flex={1}>
        <ArcText.h4 fontWeight={500} marginTop={2}>
          CREATE SAFE-LAND ZONE
        </ArcText.h4>
        <SafeLandZoneForm
          control={control}
          setValue={setValue}
          register={register}
          getValues={getValues}
          formState={{ errors } as FormState<ICreateSafeLandZoneForm>}
          watch={watch}
        />
      </ArcStack>

      <ArcStack spacing={2} direction="row" px={1}>
        <ArcButton.ghost fullWidth size="large" onClick={onCancel}>
          Cancel
        </ArcButton.ghost>
        <ArcButton.brand
          fullWidth
          size="large"
          type="submit"
          isLoading={isLoading}
          onClick={handleSubmit(onSubmit)}
        >
          Create
        </ArcButton.brand>
      </ArcStack>
    </ArcStack>
  );
};
